// Windsurf generated file
import { useState, useRef, useCallback } from 'react'
import { Box, Typography, Button, TextField } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { ProposalsApi } from '../../../api/proposals-api'
import './proposal-detail-page.scss'

interface ProposalDescriptionSectionProps {
  proposalId: string
  description?: string
  onUpdate: () => void
  onShowSnackbar: (message: string, severity: 'success' | 'error') => void
}

export function ProposalDescriptionSection({
  proposalId,
  description,
  onUpdate,
  onShowSnackbar,
}: ProposalDescriptionSectionProps) {
  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [editedDescription, setEditedDescription] = useState('')
  const [isSavingDescription, setIsSavingDescription] = useState(false)
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null)

  const handleEditDescription = useCallback(() => {
    setEditedDescription(description || '')
    setIsEditingDescription(true)
  }, [description])

  const handleCancelEditDescription = useCallback(() => {
    setIsEditingDescription(false)
    setEditedDescription('')
  }, [])

  const handleSaveDescription = useCallback(async () => {
    if (!proposalId || isSavingDescription) return

    const descriptionValue = descriptionInputRef.current?.value ?? ''

    setIsSavingDescription(true)
    try {
      const success = await ProposalsApi.updateProposalDescription(
        proposalId,
        descriptionValue
      )
      if (success) {
        setIsEditingDescription(false)
        onShowSnackbar('Description updated successfully', 'success')
        setTimeout(() => {
          onUpdate()
        }, 500)
      } else {
        onShowSnackbar(
          'Failed to update description. Please try again.',
          'error'
        )
      }
    } catch (error) {
      console.error('Error updating description:', error)
      onShowSnackbar(
        'An error occurred while updating the description.',
        'error'
      )
    } finally {
      setIsSavingDescription(false)
    }
  }, [proposalId, isSavingDescription, onUpdate, onShowSnackbar])

  return (
    <>
      <Typography
        variant="subtitle2"
        className="info-card-label"
        sx={{ pb: '0.5rem' }}
      >
        Notes for Bidders
      </Typography>
      <Box>
        <TextField
          fullWidth
          multiline
          rows={8}
          value={isEditingDescription ? editedDescription : description || ''}
          onChange={(e) => {
            const newValue = e.target.value
            if (newValue.length <= 2000) {
              setEditedDescription(newValue)
            }
          }}
          size="small"
          disabled={!isEditingDescription}
          helperText={
            isEditingDescription
              ? `These notes will be shared with all selected issuers when the bid request is sent. (${editedDescription.length}/2000 characters)`
              : undefined
          }
          error={isEditingDescription && editedDescription.length > 2000}
          inputProps={{
            maxLength: 2000,
            'aria-label': 'Description',
          }}
          inputRef={descriptionInputRef}
          placeholder={
            !isEditingDescription && (description ?? '').trim() === ''
              ? 'No notes provided'
              : undefined
          }
        />
        <Box
          className="button-container"
          sx={{ mt: isEditingDescription ? 0 : '20px' }}
        >
          {isEditingDescription ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelEditDescription}
                disabled={isSavingDescription}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => void handleSaveDescription()}
                disabled={
                  isSavingDescription || editedDescription.length > 2000
                }
              >
                Save
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEditDescription}
            >
              Edit Notes
            </Button>
          )}
        </Box>
      </Box>
    </>
  )
}
